/**
 * Admin panel for Partnerships (Certified teachers + Organizations).
 *
 * Features:
 *   • Two tabs: teachers / organizations
 *   • Drag-and-drop image upload (FileReader → base64 data URL)
 *   • Edit / toggle active / delete / change display order
 *   • All changes go through the admin API; images live in Mongo as data URLs
 *
 * Constraints mirrored from the backend:
 *   • Max ~1.2 MB per image (raw, after base64 decode)
 *   • Allowed MIMEs: image/jpeg, image/png, image/webp, image/gif, image/svg+xml
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";

const MAX_IMAGE_BYTES = 1_200_000;
const ALLOWED_MIME = new Set([
  "image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml",
]);

export default function PartnershipsPanel({ token }) {
  const [kind, setKind] = useState("teacher");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // object to edit, or "new" sentinel

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await api.adminListPartnerships(token, kind);
      setItems(d || []);
    } catch (e) {
      setError(e.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, [token, kind]);

  useEffect(() => { refresh(); }, [refresh]);

  async function onSaved() {
    setEditing(null);
    await refresh();
  }

  async function onDelete(p) {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    try {
      await api.adminDeletePartnership(token, p.id);
      setItems((xs) => xs.filter((x) => x.id !== p.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.message || "Could not delete");
    }
  }

  async function onToggleActive(p) {
    try {
      const updated = await api.adminUpdatePartnership(token, p.id, { active: !p.active });
      setItems((xs) => xs.map((x) => (x.id === p.id ? updated : x)));
    } catch (e) {
      toast.error(e.message || "Could not update");
    }
  }

  async function onReorder(p, delta) {
    try {
      const next = Math.max(0, (p.display_order || 0) + delta);
      const updated = await api.adminUpdatePartnership(token, p.id, { display_order: next });
      setItems((xs) => {
        const replaced = xs.map((x) => (x.id === p.id ? updated : x));
        return [...replaced].sort(byOrder);
      });
    } catch (e) {
      toast.error(e.message || "Could not update order");
    }
  }

  return (
    <section className="mt-12" data-testid="partnerships-panel">
      <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
        <h2 className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60">
          Partnerships
        </h2>
        <div className="flex items-center gap-2">
          <Tab active={kind === "teacher"} onClick={() => setKind("teacher")} testid="partnerships-tab-teacher">
            Teachers · {items.length && kind === "teacher" ? items.length : ""}
          </Tab>
          <Tab active={kind === "organization"} onClick={() => setKind("organization")} testid="partnerships-tab-organization">
            Organizations · {items.length && kind === "organization" ? items.length : ""}
          </Tab>
          <button
            onClick={() => setEditing("new")}
            data-testid="partnerships-add-btn"
            className="ml-2 px-3 py-1.5 bg-[#1C1C1C] text-[#FAF9F6] rounded-md text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-[#1C1C1C]/85"
          >
            + Add
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-[#B83A3A]/10 text-[#B83A3A] rounded-xl text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-10 text-center text-[#1C1C1C]/60">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-10 text-center text-[#1C1C1C]/60">
          No {kind === "teacher" ? "teachers" : "organizations"} yet. Click <span className="font-medium">+ Add</span> to create one.
        </div>
      ) : (
        <ul className="space-y-3">
          {[...items].sort(byOrder).map((p) => (
            <li
              key={p.id}
              className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-4 md:p-5 flex items-start gap-4"
              data-testid={`partnerships-row-${p.id}`}
            >
              <Thumb src={p.image_data_url} />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-[#1C1C1C]">{p.name}</div>
                {p.about && (
                  <p className="mt-1 text-sm text-[#1C1C1C]/70 line-clamp-2">{p.about}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/50">
                  <span>order {p.display_order}</span>
                  <span>· {p.active ? "active" : "hidden"}</span>
                  {p.link_url && <span className="truncate max-w-[220px]">· {p.link_url}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <div className="flex gap-1">
                  <IconBtn title="Move up" onClick={() => onReorder(p, -1)} data-testid={`partnerships-up-${p.id}`}>↑</IconBtn>
                  <IconBtn title="Move down" onClick={() => onReorder(p, +1)} data-testid={`partnerships-down-${p.id}`}>↓</IconBtn>
                </div>
                <button
                  onClick={() => onToggleActive(p)}
                  data-testid={`partnerships-toggle-${p.id}`}
                  className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C]"
                >
                  {p.active ? "Hide" : "Show"}
                </button>
                <button
                  onClick={() => setEditing(p)}
                  data-testid={`partnerships-edit-${p.id}`}
                  className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C]"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(p)}
                  data-testid={`partnerships-delete-${p.id}`}
                  className="text-[10px] tracking-[0.22em] uppercase text-[#B83A3A]/70 hover:text-[#B83A3A]"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <EditModal
          token={token}
          kind={kind}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </section>
  );
}

function byOrder(a, b) {
  if ((a.display_order || 0) !== (b.display_order || 0)) return (a.display_order || 0) - (b.display_order || 0);
  return new Date(b.created_at) - new Date(a.created_at);
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-md text-[10px] tracking-[0.24em] uppercase font-medium transition-colors ${
        active
          ? "bg-[#1C1C1C] text-[#FAF9F6]"
          : "bg-white border border-[#1C1C1C]/15 text-[#1C1C1C]/70 hover:border-[#1C1C1C]/40"
      }`}
    >
      {children}
    </button>
  );
}

function IconBtn({ children, ...rest }) {
  return (
    <button
      {...rest}
      className="w-7 h-7 flex items-center justify-center rounded-md bg-[#FAF9F6] border border-[#1C1C1C]/10 text-xs text-[#1C1C1C]/70 hover:border-[#1C1C1C]/40"
    >
      {children}
    </button>
  );
}

function Thumb({ src }) {
  if (!src) {
    return (
      <div className="w-16 h-16 rounded-lg bg-[#FAF9F6] border border-[#1C1C1C]/10 flex items-center justify-center shrink-0">
        <span className="text-[8px] tracking-[0.22em] uppercase text-[#1C1C1C]/35">no img</span>
      </div>
    );
  }
  return <img src={src} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0 bg-[#FAF9F6]" />;
}

/* -------------- Edit / create modal -------------- */

function EditModal({ token, kind, item, onClose, onSaved }) {
  const isNew = !item;
  const [name, setName] = useState(item?.name || "");
  const [about, setAbout] = useState(item?.about || "");
  const [linkUrl, setLinkUrl] = useState(item?.link_url || "");
  const [image, setImage] = useState(item?.image_data_url || "");
  const [displayOrder, setDisplayOrder] = useState(item?.display_order ?? 0);
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState("");
  const [formError, setFormError] = useState("");
  const [dragging, setDragging] = useState(false);

  const imageBytes = useMemo(() => {
    if (!image || !image.startsWith("data:")) return 0;
    const i = image.indexOf(",");
    if (i < 0) return 0;
    const b64 = image.slice(i + 1);
    return Math.floor(b64.length * 0.75);
  }, [image]);

  async function handleFile(file) {
    setImgError("");
    if (!file) return;
    if (!ALLOWED_MIME.has(file.type)) {
      setImgError("Unsupported image type. Use JPEG, PNG, WebP, GIF, or SVG.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImgError(`Image is too large (${Math.round(file.size / 1024)} KB). Max ${Math.round(MAX_IMAGE_BYTES / 1024)} KB.`);
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
    setImage(dataUrl);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  async function submit(e) {
    e.preventDefault();
    setFormError("");
    if (!name.trim()) { setFormError("Name is required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        about: about.trim(),
        link_url: linkUrl.trim() || null,
        image_data_url: image || null,
        display_order: Number(displayOrder) || 0,
      };
      if (isNew) {
        await api.adminCreatePartnership(token, { kind, active: true, ...payload });
        toast.success("Created");
      } else {
        await api.adminUpdatePartnership(token, item.id, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (err) {
      setFormError(err.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="partnerships-edit-modal">
      <div className="w-full max-w-lg bg-white rounded-t-3xl md:rounded-3xl p-6 md:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl text-[#1C1C1C]">
            {isNew ? "New" : "Edit"} {kind === "teacher" ? "certified teacher" : "organization"}
          </h3>
          <button onClick={onClose} className="text-[#1C1C1C]/60 hover:text-[#1C1C1C]">✕</button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {/* Image drop zone */}
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-2">Image</div>
            <label
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              data-testid="partnerships-dropzone"
              className={`block cursor-pointer rounded-2xl border-2 border-dashed ${
                dragging ? "border-[#7FA6C8] bg-[#7FA6C8]/5" : "border-[#1C1C1C]/15 bg-[#FAF9F6]"
              } p-4 text-center transition-colors`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                className="hidden"
                data-testid="partnerships-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {image ? (
                <div className="flex flex-col items-center gap-3">
                  <img src={image} alt="preview" className="max-h-40 rounded-lg object-contain" />
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/50">
                    {imageBytes > 0 ? `${Math.round(imageBytes / 1024)} KB` : ""} · click or drop to replace
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setImage(""); }}
                    className="text-[10px] tracking-[0.24em] uppercase text-[#B83A3A]/75 hover:text-[#B83A3A]"
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <div className="py-6">
                  <div className="text-sm text-[#1C1C1C]/70">Drop an image here, or click to choose.</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/45 mt-1">
                    JPEG · PNG · WebP · GIF · SVG — max {Math.round(MAX_IMAGE_BYTES / 1024)} KB
                  </div>
                </div>
              )}
            </label>
            {imgError && (
              <div className="text-sm text-[#B83A3A] mt-2" data-testid="partnerships-img-error">{imgError}</div>
            )}
          </div>

          <Field label="Name">
            <input
              required maxLength={160}
              value={name} onChange={(e) => setName(e.target.value)}
              className="input"
              data-testid="partnerships-name"
            />
          </Field>

          <Field label="About (short, shown on the card)">
            <textarea
              rows={4} maxLength={1500}
              value={about} onChange={(e) => setAbout(e.target.value)}
              className="input resize-y"
              data-testid="partnerships-about"
            />
          </Field>

          <Field label="Website (optional)">
            <input
              type="url" maxLength={500}
              value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="input"
              data-testid="partnerships-link"
            />
          </Field>

          <Field label="Display order (smaller = shown first)">
            <input
              type="number" min={0} max={9999}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              className="input"
              data-testid="partnerships-order"
            />
          </Field>

          {formError && (
            <div className="text-sm text-[#B83A3A]" data-testid="partnerships-form-error">{formError}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit" disabled={saving}
              data-testid="partnerships-submit"
              className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40"
            >
              {saving ? "Saving…" : isNew ? "Create" : "Save changes"}
            </button>
            <button
              type="button" onClick={onClose}
              className="px-5 py-3 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40"
            >
              Cancel
            </button>
          </div>
        </form>

        <style>{`.input{width:100%;padding:.7rem 1rem;background:#FAF9F6;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:0.95rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4);background:white}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-2">{label}</div>
      {children}
    </label>
  );
}
